import {
  useSetupExchange,
  useSetupUpbitAnnouncementKey,
  useUpbitAnnouncementKey,
} from '@/hooks/useExchanges';
import { useUserProxyIp } from '@/hooks/useProxy';
import { UPBIT_CODE, getExchangeConfig } from '@/utils/exchange-config';

/**
 * What the connect drawer/dialog needs for one exchange: its config (with
 * the IP to allowlist) and a submit that goes to the right endpoint.
 * Upbit keys go to the announcement-key endpoint and IP-allow the scanner
 * host; every other exchange uses the trading setup and the user's proxy IP.
 */
export const useExchangeSetup = (exchangeCode: string, userId: string) => {
  const isUpbit = exchangeCode === UPBIT_CODE;
  const setupMutation = useSetupExchange();
  const upbitMutation = useSetupUpbitAnnouncementKey();
  const { data: userProxyIp } = useUserProxyIp(isUpbit ? undefined : userId);
  const { data: upbitKey } = useUpbitAnnouncementKey(userId, isUpbit);

  const whitelistIp = isUpbit
    ? upbitKey?.allowlistIps.join(', ')
    : userProxyIp || undefined;
  const config = getExchangeConfig(exchangeCode, whitelistIp);

  const submit = async (apiKey: string, apiSecret: string, apiPassphrase: string) => {
    if (isUpbit) {
      await upbitMutation.mutateAsync({ userId, apiKey, apiSecret });
      return;
    }
    await setupMutation.mutateAsync({
      userId,
      exchangeCode,
      apiKey,
      apiSecret,
      apiPassphrase,
    });
  };

  return {
    config,
    submit,
    isPending: isUpbit ? upbitMutation.isPending : setupMutation.isPending,
  };
};
